import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { stringify } from '../../../../util/urlHelpers';
import { useHorizontalScroller } from '../../../../util/useHorizontalScroller';
import { landingSectionSurfaceClassName } from '../../../LandingPage/landingSectionSurface';

import { ListingCard, NamedLink } from '../../../../components';

import css from './SectionLandingListingRows.module.css';

// `sort=createdAt` is the search page's default order anyway; in the link it only made each
// "view all" a parameter variant of the category URL the search page names as canonical.
const DEFAULT_SORT = 'createdAt';

const hasMessage = (intl, id) =>
  !!intl.messages && Object.prototype.hasOwnProperty.call(intl.messages, id);

const LANDING_CARD_RENDER_SIZES = [
  '(max-width: 767px) 70vw',
  '(max-width: 1023px) 38vw',
  '(max-width: 1439px) 26vw',
  '260px',
].join(', ');

/**
 * One horizontal listing row (title, “view all”, scrollable cards).
 *
 * @param {Object} props
 * @param {Object} props.row - landingPage.listingRows entry
 * @returns {JSX.Element|null}
 */
const LandingListingRow = ({ row, isFirstRow }) => {
  const intl = useIntl();

  const rowState = useSelector(state => state.LandingPage?.listingRows?.[row.id] || {});
  const { ids = [], viewAllSearchParams = {} } = rowState;

  const listings = useSelector(state => getListingsById(state, ids));

  const { scrollerRef, canScrollLeft, canScrollRight, scrollByPage } = useHorizontalScroller(
    listings.length
  );

  // Render whenever we already hold listings, even while a refetch is in flight. The reducer
  // preserves `ids` on both REQUEST and ERROR, so gating on `inProgress`/`error` would unmount
  // ~950 server-rendered nodes right after hydration and rebuild them ~600ms later, purely
  // because Routes.js re-runs loadData on mount. Measured cost of that teardown: CLS 0.45 -> 0.
  const showRow = listings.length > 0;
  if (!showRow) {
    return null;
  }

  const { sort, ...filterParams } = viewAllSearchParams;
  const searchString = stringify(
    sort && sort !== DEFAULT_SORT ? viewAllSearchParams : filterParams
  );
  const viewAllTo = { search: searchString ? `?${searchString}` : '' };
  // The link names what it leads to ("Alle Tagespässe") instead of a bare "Alle anzeigen" three
  // times on one page — anchor text is how a crawler learns what the target is about. A row
  // without its own label keeps the generic one.
  const viewAllLabelId = hasMessage(intl, `SectionLandingListingRows.viewAll.${row.id}`)
    ? `SectionLandingListingRows.viewAll.${row.id}`
    : 'SectionLandingListingRows.viewAll';

  return (
    <div className={css.row}>
      <div className={css.rowHeader}>
        <h2 className={css.rowTitle}>
          <FormattedMessage id={row.titleTranslationKey} />
        </h2>
        <NamedLink
          name="SearchPage"
          to={viewAllTo}
          className={css.viewAll}
          dataTrackCta="row_view_all"
        >
          <span className={css.viewAllLabel}>
            <FormattedMessage id={viewAllLabelId} />
          </span>
          <ChevronRight className={css.viewAllIcon} aria-hidden="true" />
        </NamedLink>
      </div>
      <div className={css.scrollerWrap}>
        <button
          type="button"
          className={classNames(css.navButton, css.navButtonLeft, {
            [css.navButtonHidden]: !canScrollLeft,
          })}
          aria-label={intl.formatMessage({ id: 'SectionLandingListingRows.scrollPrevLabel' })}
          onClick={scrollByPage(-1)}
          tabIndex={canScrollLeft ? 0 : -1}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
        <button
          type="button"
          className={classNames(css.navButton, css.navButtonRight, {
            [css.navButtonHidden]: !canScrollRight,
          })}
          aria-label={intl.formatMessage({ id: 'SectionLandingListingRows.scrollNextLabel' })}
          onClick={scrollByPage(1)}
          tabIndex={canScrollRight ? 0 : -1}
        >
          <ChevronRight aria-hidden="true" />
        </button>
        <div className={css.scroller} ref={scrollerRef}>
          <ul
            className={css.list}
            aria-label={intl.formatMessage(
              { id: 'SectionLandingListingRows.rowLabel' },
              { title: intl.formatMessage({ id: row.titleTranslationKey }) }
            )}
          >
            {listings.map((listing, index) => (
              <li key={listing.id.uuid} className={css.cardWrap}>
                <div className={css.cardShell}>
                  <ListingCard
                    className={css.listingCard}
                    listing={listing}
                    renderSizes={LANDING_CARD_RENDER_SIZES}
                    intl={intl}
                    highImagePriority={isFirstRow && index === 0}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Airbnb-style horizontal listing highlights on the landing page.
 *
 * @param {Object} props
 * @param {string} [props.sectionId]
 * @param {string[]} [props.rowIds] - If set, only these row `id`s from config (in order); otherwise all rows.
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @returns {JSX.Element|null}
 */
const SectionLandingListingRows = props => {
  const { sectionId, className, rootClassName, rowIds: rowIdsProp, landingSurface } = props;
  const config = useConfiguration();
  const allRows = config.landingPage?.listingRows || [];
  const rows =
    Array.isArray(rowIdsProp) && rowIdsProp.length > 0
      ? rowIdsProp.map(id => allRows.find(r => r.id === id)).filter(Boolean)
      : allRows;

  const listingRowsState = useSelector(state => state.LandingPage?.listingRows || {});

  // Must use the same predicate as the per-row gate above — otherwise this outer gate unmounts
  // the whole <section> while the rows themselves would happily keep rendering.
  const hasAnyVisibleRow = rows.some(row => {
    const r = listingRowsState[row.id];
    return r && (r.ids || []).length > 0;
  });

  if (!rows.length || !hasAnyVisibleRow) {
    return null;
  }

  return (
    <section
      id={sectionId}
      className={classNames(
        css.root,
        landingSectionSurfaceClassName(landingSurface),
        rootClassName,
        className
      )}
    >
      <div className={css.wrapper}>
        {rows.map((row, rowIndex) => (
          <LandingListingRow key={row.id} row={row} isFirstRow={rowIndex === 0} />
        ))}
      </div>
    </section>
  );
};

export default SectionLandingListingRows;
